import { useDigitsContext } from 'context/digits-context'
import { usePlayerContext } from 'context/player-context'
import { MotionValue, motion, useTransform } from 'framer-motion'
import { FC, useCallback } from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'

import DeviceSrc from './assets/device-mobile.png'
import GlassSrc from './assets/glass.png'
import KnobSrc from './assets/knob.png'
import ShadowSrc from './assets/shadow.png'

const Svg = styled.svg`
  display: block;
  position: absolute;
  top: var(--header-height);
  left: 0;
  width: 100%;
  height: calc(100% - var(--header-height));
  user-select: none;
  touch-action: none;

  .digit {
    font-family: Oswald;
    font-size: 44px;
    fill: black;
    letter-spacing: -0.04em;
  }

  /* body.no-touch & g {
    transition: all 0.3s;
  } */
`

type Props = {
  progress: MotionValue<number>
  onScroll: (delta: number) => void
}

export const DisplayMobile: FC<Props> = ({ progress, onScroll }) => {
  const { timelineCoords, width, mapYearToCoords } = useDigitsContext()
  const { isMuted, toggleMuted } = usePlayerContext()
  const transform = useTransform(
    progress,
    [0, width],
    [-150, -width * 1.7 - 150]
  )

  const linkClickHandler = useCallback(
    (year: number | string) => {
      const newValue = mapYearToCoords(String(year))

      if (newValue) {
        progress.set(newValue)
      }
    },
    [progress, mapYearToCoords]
  )

  return (
    <Svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 609 1035">
      <defs>
        <clipPath id="clip-path">
          <rect
            id="Rectangle_11"
            data-name="Rectangle 11"
            width="506"
            height="972"
            transform="translate(0 -972)"
            fill="none"
          />
        </clipPath>
        <clipPath id="clip-path-2">
          <rect
            id="Rectangle_14"
            data-name="Rectangle 14"
            width="398"
            height="158"
            transform="translate(87 164)"
            fill="#fff"
          />
        </clipPath>
        <clipPath id="clip-path-3">
          <rect
            id="Rectangle_13"
            data-name="Rectangle 13"
            width="399"
            height="158"
            transform="translate(0 -158)"
            fill="none"
          />
        </clipPath>
        <filter
          id="cursor"
          x="181.5"
          y="162.5"
          width="18"
          height="128"
          filterUnits="userSpaceOnUse"
        >
          {/* <feOffset dx="2" dy="2" input="SourceAlpha" /> */}
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feFlood floodOpacity="0.396" />
          <feComposite operator="in" in2="blur" />
          <feComposite in="SourceGraphic" />
        </filter>
      </defs>
      <g>
        <image
          data-name="shadow"
          width="609"
          height="147"
          y="888"
          href={ShadowSrc}
        />
        <g transform="translate(432 178)">
          <motion.image
            data-name="knob"
            width="132"
            height="132"
            href={KnobSrc}
            style={{
              rotateZ: transform,
              cursor: 'ns-resize',
            }}
            onPan={(_evt, info) => onScroll(info.delta.y)}
          />
        </g>
        <g style={{ pointerEvents: 'none' }}>
          <g data-name="device" transform="translate(44 970)">
            <g clipPath="url(#clip-path)">
              <image
                width="506"
                height="972"
                transform="translate(0 -972)"
                href={DeviceSrc}
              />
            </g>
          </g>

          <motion.rect
            data-name="draggable-area"
            width="398"
            height="158"
            transform="translate(87 164)"
            onPan={(_evt, info) => onScroll(-info.delta.x)}
            style={{ pointerEvents: 'auto', cursor: 'ew-resize' }}
            fill="transparent"
          />

          <g
            id="ui-masked"
            transform="translate(15 1)"
            clipPath="url(#clip-path-2)"
          >
            <g id="ui" transform="translate(-15 -1)" opacity="0.697">
              <g id="timeline">
                <rect
                  id="Rectangle_9"
                  data-name="Rectangle 9"
                  width="1347"
                  height="15"
                  x="55"
                  y="238"
                />
                <line
                  id="Line_1"
                  data-name="Line 1"
                  x2="1403"
                  y1="231"
                  y2="231"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                />
                <line
                  id="Line_2"
                  data-name="Line 2"
                  x2="1346.525"
                  y1="261"
                  y2="261"
                  fill="none"
                  stroke="#000"
                  strokeWidth="2"
                />
              </g>

              <motion.g
                style={{
                  translateX: transform,
                  pointerEvents: 'auto',
                  cursor: 'ew-resize',
                }}
                onPan={(_evt, info) => onScroll(-info.delta.x)}
              >
                {timelineCoords.map((coord, i) => (
                  <Link
                    to={`/year/${coord.value}`}
                    key={coord.value}
                    onClick={() => linkClickHandler(coord.value)}
                  >
                    <text
                      x={coord.x * 1.7}
                      y={i % 2 ? 220 : 310}
                      className="digit"
                    >
                      {coord.value}
                    </text>
                  </Link>
                ))}
              </motion.g>
            </g>
          </g>
        </g>
        <g
          data-name="glass"
          transform="translate(30 968)"
          style={{
            mixBlendMode: 'screen',
            isolation: 'isolate',
            pointerEvents: 'none',
          }}
        >
          <g style={{ mixBlendMode: 'screen', isolation: 'isolate' }}>
            <g transform="translate(71 -647)">
              <g clipPath="url(#clip-path-3)">
                <image
                  width="399"
                  height="158"
                  transform="translate(0 -158)"
                  href={GlassSrc}
                />
              </g>
            </g>
          </g>
        </g>
        <g transform="matrix(1, 0, 0, 1, -15, -1)" filter="url(#cursor)">
          <rect
            data-name="cursor"
            width="9"
            height="119"
            transform="translate(184 165)"
            fill="#b23b31"
          />
        </g>
      </g>

      <g id="ON" transform="translate(400 407)">
        <g transform={isMuted ? 'translate(57 20) rotate(180 0 0)' : 'none'}>
          <image
            id="tlacitko"
            width="57"
            height="20"
            xlinkHref="data:image/png;base64,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"
          />
          <image
            id="Vrstva_3"
            data-name="Vrstva 3"
            width="4"
            height="20"
            xlinkHref="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAQAAAAUCAYAAACqJ5zlAAAABHNCSVQICAgIfAhkiAAAACZJREFUGFdjVFNTu8nAwKAGxCDwkBEocBvIUIEKPBkVICI8UIIQAAjSLrPIQJSOAAAAAElFTkSuQmCC"
          />
        </g>
      </g>

      <rect
        width="200"
        height="58"
        x="282"
        y="390"
        onClick={toggleMuted}
        style={{ cursor: 'pointer' }}
        fill="rgba(0,0,0,0.01)"
      />
    </Svg>
  )
}
